import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
// Materials
import {
	GridActionsCellItem,
	GridColDef,
	GridRowId,
	GridRowModes,
	GridRowModesModel,
	GridRowParams,
	GridRowSelectionModel,
	GridValidRowModel,
	MuiEvent,
	useGridApiRef,
} from "@mui/x-data-grid-premium";
// Icons
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";
import CancelIcon from "@mui/icons-material/Close";
import CheckGray from "@/Washouts/assets/icons/CheckGray.svg";
import CheckBlue from "@/Washouts/assets/icons/CheckBlue.svg";
import RolloverGray from "@/Washouts/assets/icons/RolloverGray.svg";
import RolloverBlue from "@/Washouts/assets/icons/RolloverBlue.svg";
// Helpers
import { accountFormat } from "@/Common/helpers/number";
import RowFeedback from "@/Common/helpers/RowFeedback";
// Components
import { emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
// API
import { retrieveAdjustmentCount } from "@/Washouts/api/PayrollClosing/queries";
import { createAdjustments, deleteAdjustment, updateAdjustment } from "@/Washouts/api/PayrollClosing/mutations";
// Components
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import SelectionDialog from "@/Layouts/components/Dialogs/SelectionDialog";
import InputEditCell from "./InputEditCell";
import InputEditUserCell from "./InputEditUserCell";
import InputEditInputCell from "./InputEditInputCell";
import { errorLogger } from "@/Common/helpers/logger";
// Types
import { RootState } from "@/redux/store";
import { ICreateAdjustmentsInput, TUpdateAdjustmentInput } from "@/Washouts/types/PayrollClosing/mutations";
import { IAdjustmentItem } from "@/Washouts/types/PayrollClosing/queries";
import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";
import { IStatus } from "@/Common/api/types/CommissionCycle";
import { DATE_FORMAT } from "@/Constants/date";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { useLocation } from "@/Common/hooks/Location/useLocation";

const headerClassNameLabel = "table-header-top-aligned show-as-column";
const CUSTOM_ICON_PROPS = {
	typographyClassName: "button-icon-text !font-sans !font-medium !text-[14.5px] text-center text-gray-text",
};
const DEFAULT_ICON_CLASSNAME = "size-[32px] m-auto";

const handleRowEditStart = (editModeActive: boolean, event: MuiEvent & { defaultPrevented: boolean }) => {
	if (editModeActive) {
		event.defaultPrevented = true;
		event.defaultMuiPrevented = true;
	}
};

const handleRowEditStop = (_: GridRowParams, event: MuiEvent) => {
	event.defaultMuiPrevented = true;
};

const handleProcessRowUpdateError = (error: Error) => {
	// Here you should handle the error.
	errorLogger(error);
};

type CommissionCycleAdjustmentTableProps = {
	data?: IAdjustmentItem[];
	locationId: string;
	payPeriodId: string;
	canCreateAdjustments: boolean;
	positionStatus: IStatus[];
};

export default function CommissionCycleAdjustmentTable(props: Readonly<CommissionCycleAdjustmentTableProps>) {
	const { data = [], locationId, canCreateAdjustments, payPeriodId } = props;

	const apiRef = useGridApiRef();
	const user = useSelector((state: RootState) => state.user.user);

	const [openDialog, setOpenDialog] = useState(false);
	const [rows, setRows] = useState(data ?? []);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const [selectedRows, setSelectedRows] = useState<readonly GridRowId[]>([]);

	const { timezone } = useLocation();

	const positionsToFilter = useMemo(
		() => props.positionStatus.filter((position) => position.status === "POSTED_PAYROLL").map((position) => position.position),
		[props.positionStatus]
	);

	// Queries / Mutations
	// Query for getting the adjustmentCount to generate the new ID
	const {
		data: retrieveData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["adjustmentCount", locationId],
		queryFn: () => retrieveAdjustmentCount(locationId),
	});

	// Mutations
	const { mutate: saveAdjustment } = useMutation({
		mutationFn: (params: { input: ICreateAdjustmentsInput; updatedRowId: string }) => {
			RowFeedback.skeleton(params.updatedRowId, apiRef)?.display();
			return createAdjustments({ locationId, payPeriodId, input: params.input });
		},
		onSuccess: (result, { updatedRowId }) => {
			const addedRow = result?.adjustments[0];
			RowFeedback.skeleton(updatedRowId, apiRef)?.success();

			const updated = [...rows];
			if (addedRow) {
				updated[updated.length - 1] = addedRow;
				setRows(updated);
			}
			return addedRow;
		},
		onError: (error, { updatedRowId }) => {
			RowFeedback.skeleton(updatedRowId, apiRef)?.error();
			errorLogger(error);
		},
	});

	const { mutate: updateAdjustments } = useMutation({
		mutationFn: (input: { updatedRowId: string; adjustment: TUpdateAdjustmentInput }) =>
			updateAdjustment({ locationId, payPeriodId, input: input.adjustment, id: input.updatedRowId }),
		onSuccess: (result, { updatedRowId }) => {
			const updatedRow = result?.adjustments[0];
			if (updatedRow) {
				const updated = [...rows];
				updated.forEach((element, key) => {
					if (element._id === updatedRow._id) {
						updated[key] = updatedRow;
					}
				});
				setRows(updated);
			}

			RowFeedback.skeleton(updatedRowId, apiRef)?.success();
		},
		onError: (error, { updatedRowId }) => {
			RowFeedback.skeleton(updatedRowId, apiRef)?.error();
			errorLogger(error);
		},
	});

	const { mutate: removeAdjustments, isPending: removing } = useMutation({
		mutationFn: ({ ids, userId }: { ids: string[]; userId: string }) =>
			deleteAdjustment({ ids, locationId, payPeriodId, userName: userId }),
	});

	const onAddAdjustment = (value: string | null) => {
		setOpenDialog(false);
		if (value) {
			const isRollover = value?.toUpperCase() === "ROLLOVER";
			const key = uuidv4();
			// Define a default row initial values.
			const newRow: IAdjustmentItem = {
				_id: key,
				isNew: true,
				createdOn: new Date(),
				createdBy: user as { _id: string; fullName: string },
				description: "",
				amount: 0,
				checkNumber: "0",
				adjustmentType: isRollover ? retrieveData?.adjustmentCount.toString().padStart(10, "0") : value,
			};

			// Add the new row to the rows state.
			setRows((prev) => [...prev, newRow]);

			// Set the new row in edit mode.
			setRowModesModel((prev) => ({
				...prev,
				[key]: { mode: GridRowModes.Edit },
			}));
		}
	};

	const onCloseDialog = () => {
		setOpenDialog(false);
	};

	// This fn is called when the user clicks on the "Save" button.
	const handleSaveClick = (id: string) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => row[key].error || !row[key].value);

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[id]: { mode: GridRowModes.View },
			}));
		} else if (errors?.length === keys.length) {
			apiRef.current.setState((state) => ({
				...state,
				editRows: {
					...state.editRows,
					[id]: {
						...state.editRows[id],
						...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[id][key], error: true } }), {}),
					},
				},
			}));
		}
	};

	// This fn is called when the user clicks on the "Cancel" button
	const handleCancelClick = (id: string) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	// This fn is called when the user clicks on the "Delete" button.
	const handleDeleteClick = async (ids: GridRowId[]) => {
		ids.forEach((id) => {
			RowFeedback.skeleton(id, apiRef)?.delete()?.display();
		});
		const idsMapped = ids.map((id) => id.toString());

		removeAdjustments(
			{
				ids: idsMapped,
				userId: user?._id ?? "",
			},
			{
				onSuccess: () => {
					setRows((prev) => prev.filter((row) => !idsMapped.includes(row._id)));
					setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
				},
				onError: () => {
					ids.forEach((id) => {
						RowFeedback.skeleton(id, apiRef)?.errorDelete();
					});
				},
			}
		);
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleAddRow = () => {
		if (isLoading || isFetching) {
			return;
		}
		setOpenDialog(true);
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection: GridRowSelectionModel) => {
		setSelectedRows(newSelection);
	};

	// This fn is call after the row is added or edited and it should be used to save data to the backend.
	const handleProcessRowUpdate = async (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
		// compare the old row with the new row to know if changes were made.
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const { _id } = newRow;

		const updatedRow: GridValidRowModel = {
			_id,
			isNew: false,
			createdOn: new Date(),
			recipient: newRow.recipient,
			description: newRow.description,
			amount: Number(newRow.amount),
			checkNumber: newRow.checkNumber,
		};

		// Here you should save the data to the backend.
		RowFeedback.skeleton(updatedRow._id, apiRef)?.display();

		if (newRow?.isNew) {
			const input: ICreateAdjustmentsInput = {
				adjustmentType: newRow.adjustmentType,
				amount: Number(newRow.amount),
				checkNumber: newRow.checkNumber,
				description: newRow.description,
				createdBy: user?._id ?? "",
				createdOn: new Date(),
				lastModifiedOn: new Date(),
				lastModifiedBy: user?._id ?? "",
				recipient: newRow.recipient?._id ?? "",
			};
			saveAdjustment({ input, updatedRowId: updatedRow._id });
		} else {
			const input: TUpdateAdjustmentInput = {
				amount: Number(newRow.amount),
				checkNumber: newRow.checkNumber,
				description: newRow.description,
				recipient: newRow.recipient?._id ?? "",
			};

			updateAdjustments({ adjustment: input, updatedRowId: newRow._id });
		}

		return updatedRow;
	};

	const columns: GridColDef[] = [
		{ field: "_id", headerName: "ID", filterable: false },
		{
			field: "createdOn",
			headerName: "Entry Date",
			type: "date",
			width: 130,
			filterable: false,
			resizable: false,
			headerClassName: headerClassNameLabel,
			valueFormatter: (param: string) =>
				param === "Now" ? "Now" : getFormattedDate(param, timezone, DATE_FORMAT.usDateSlashShortYear),
		},
		{
			field: "createdBy",
			headerName: "User",
			width: 120,
			headerClassName: headerClassNameLabel,
			valueGetter: (param?: { fullName: string }) => param?.fullName ?? "",
		},
		{
			field: "recipient",
			headerName: "Recipient",
			width: 175,
			editable: true,
			headerClassName: headerClassNameLabel,
			renderEditCell: (params) => <InputEditUserCell {...params} locationId={locationId} positionsToFilter={positionsToFilter} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			valueGetter: (param?: { fullName?: string }) => param?.fullName ?? "",
		},
		{
			field: "description",
			headerName: "Description",
			width: 240,
			editable: true,
			headerClassName: headerClassNameLabel,
			renderEditCell: (params) => <InputEditCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
		},
		{
			field: "amount",
			headerName: "Amount",
			width: 100,
			align: "right",
			editable: true,
			resizable: false,
			headerClassName: headerClassNameLabel,
			cellClassName: (params) => {
				const isNegative = Math.sign(params?.value) === -1;
				return clsx({
					"cell-with-dollar-symbol no-focus": true,
					"simple-negative-value": isNegative,
				});
			},
			renderCell: ({ value }) => {
				const isNegative = Math.sign(value) === -1;
				const showValue = accountFormat(Math.abs(value));
				return <div className="MuiDataGrid-cellContent">{isNegative ? `(${showValue})` : showValue}</div>;
			},
			renderEditCell: (params) => <InputEditInputCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
		},
		{
			field: "checkNumber",
			headerName: "Adjustment Ref #",
			width: 190,
			align: "right",
			flex: 1,
			editable: true,
			resizable: false,
			headerClassName: "table-header-top-aligned table-header-align-right show-as-column",
			renderEditCell: (params) => {
				if (params?.row?.adjustmentType !== "Check") {
					return <div className="MuiDataGrid-cellContent">Roll Over ID: {`"${params.row.adjustmentType}"`}</div>;
				}
				return <InputEditCell {...params} />;
			},
			renderCell: (params) => {
				if (params.row.adjustmentType !== "Check") {
					return <div className="MuiDataGrid-cellContent">Roll Over ID: {`"${params.row.adjustmentType}"`}</div>;
				}
				return <div className="MuiDataGrid-cellContent">{`Check #: ${params?.value}`}</div>;
			},
			preProcessEditCellProps: (params) => {
				let validationOptions = null;
				if (params.row?.adjustmentType !== "Check") {
					validationOptions = { acceptZero: true };
				}
				const valueExists = emptyValidation(params.props.value, validationOptions);
				return { ...params.props, error: !valueExists };
			},
		},
		{
			field: "actions",
			type: "actions",
			width: 80,
			resizable: false,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="saveComission"
							icon={<SaveIconComponent />}
							label="Save"
							onClick={() => handleSaveClick(id.toString())}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							label="Cancel"
							className="textPrimary"
							onClick={() => handleCancelClick(id.toString())}
							color="inherit"
						/>,
					];
				}

				return [];
			},
		},
	];

	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	useEffect(() => {
		if (locationId) {
			setRows(data ?? []);
		}

		return () => {
			setRows([]);
			setOpenDialog(false);
		};
	}, [props]);

	return (
		<>
			<SelectionDialog
				fullWidth
				icons={{
					first: <img src={CheckGray} alt="check icon gray" className={DEFAULT_ICON_CLASSNAME} />,
					firstHover: <img src={CheckBlue} alt="check icon blue" className={DEFAULT_ICON_CLASSNAME} />,
					second: <img src={RolloverGray} alt="rollover icon gray" className={DEFAULT_ICON_CLASSNAME} />,
					secondHover: <img src={RolloverBlue} alt="rollover icon blue" className={DEFAULT_ICON_CLASSNAME} />,
				}}
				open={openDialog}
				sx={{
					"& .MuiPaper-root": {
						borderRadius: 4,
						maxWidth: "200px",
					},
				}}
				onClose={onCloseDialog}
				onAddAction={onAddAdjustment}
				iconsProps={{
					first: CUSTOM_ICON_PROPS,
					second: CUSTOM_ICON_PROPS,
				}}
			/>
			<DataSmartTable
				columns={columns}
				rows={rows}
				headerHeight={DEFAULT_HEADER_HEIGHT}
				rowHeight={28}
				apiRef={apiRef}
				editMode="row"
				disableColumnMenu
				disableMultipleRowSelection
				disableRowSelectionOnClick
				checkboxSelection
				isRowSelectable={() => !editModeActive && Boolean(canCreateAdjustments)}
				columnVisibilityModel={{
					_id: false,
				}}
				onRowSelectionModelChange={handleRowSelected}
				rowModesModel={rowModesModel}
				processRowUpdate={handleProcessRowUpdate}
				onProcessRowUpdateError={handleProcessRowUpdateError}
				onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
				onRowEditStart={(_, event) => handleRowEditStart(editModeActive, event)}
				onRowEditStop={handleRowEditStop}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						filter: {
							hide: !!selectedRows.length,
							disabled: editModeActive,
						},
						add: {
							onAddAction: handleAddRow,
							hide: !!selectedRows.length,
							disabled: editModeActive || !canCreateAdjustments,
						},
						delete: {
							onDeleteAction: handleDeleteClick,
							deleteTitle: "Remove",
							hide: !selectedRows.length,
							disabled: !canCreateAdjustments || removing,
						},
					},
					noRowsOverlay: {
						title: "No Adjustments Data",
					},
				}}
				hideFooter
				customProps={{
					tableContainerStyle: { height: "100%", width: "100%" },
					tableClassName: {
						"functional-table": true,
						"edit-table": true,
						"[&_.toolbar-actions]:!right-0": true,
						"!top-[-8px]": true,
						"[&_.MuiDataGrid-main]:!mt-[20px]": true,
					},
					expandableRows: true,
					hide: !rows?.length,
					tableHidedProps: {
						currentMonth: canCreateAdjustments,
						sectionFirstTitle: "No Adjustment in this list.",
						onAddTheFirstOne: handleAddRow,
					},
				}}
			/>
		</>
	);
}
